import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Avatar, Tabs, Tooltip } from 'antd'
import { Icon } from '@iconify/react'

import MentionEditor from '../../../../../components/MentionEditor/MentionEditor'
import sprintTabStyles from '../../ProjectCommentsTabs.module.css'
import StoryMapActivityTab from './StoryMapActivityTab'
import StoryMapActivityModal from './StoryMapActivityModal'
import StoryMapDeleteConfirm from './StoryMapDeleteConfirm'
import { toCommentHtml } from './toCommentHtml'
import styles from './StoryMapCardDetailModal.module.css'

const FOOTER_TABS = [
    { key: 'comments', label: 'Comments' },
    { key: 'activity', label: 'All activity' },
]

const getAvatarColor = (name = '') => {
    const colors = [
        'rgb(139, 92, 246)',
        'rgb(59, 130, 246)',
        'rgb(34, 197, 94)',
        'rgb(239, 68, 68)',
        'rgb(245, 158, 11)',
    ]
    const hash = String(name)
        .split('')
        .reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return colors[hash % colors.length]
}

const getInitials = (name = '') =>
    String(name)
        .split(' ')
        .map((part) => part[0])
        .filter(Boolean)
        .join('')
        .slice(0, 2)
        .toUpperCase()

const normalizeName = (name = '') => String(name).trim().toLowerCase()

const CommentListSkeleton = ({ rows = 3 }) => (
    <div className={styles.commentListSkeleton} aria-hidden>
        {Array.from({ length: rows }).map((_, index) => (
            <div key={index} className={styles.commentSkeletonRow}>
                <span className={styles.commentSkeletonAvatar} />
                <span className={styles.commentSkeletonLines}>
                    <span className={styles.commentSkeletonLineShort} />
                    <span className={styles.commentSkeletonLineLong} />
                </span>
            </div>
        ))}
    </div>
)

const stripCommentPlainText = (value = '') =>
    String(value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()

const StoryMapCommentsFooter = ({
    projectId,
    cardId,
    open,
    footerTab,
    onFooterTabChange,
    comments,
    loadingComments,
    loadingMoreComments,
    commentsHasMore,
    onLoadMoreComments,
    onPostComment,
    onDeleteComment,
    onEditComment,
    onLoadActivity,
    readOnly = false,
    canDelete = false,
    currentUserNames = [],
}) => {
    const commentListScrollRef = useRef(null)
    const loadMoreSentinelRef = useRef(null)
    const stickCommentsToBottomRef = useRef(true)
    const commentsScrollPreserveRef = useRef(null)
    const [mentionData, setMentionData] = useState({
        value: '',
        comment: '',
        mentionDataList: [],
    })
    const [clearEditor, setClearEditor] = useState(false)
    const [postingComment, setPostingComment] = useState(false)
    const [activityModalOpen, setActivityModalOpen] = useState(false)
    const [pendingDeleteComment, setPendingDeleteComment] = useState(null)
    const [editingCommentId, setEditingCommentId] = useState(null)
    const [editMentionData, setEditMentionData] = useState({
        value: '',
        comment: '',
        mentionDataList: [],
    })
    const [clearEditEditor, setClearEditEditor] = useState(false)
    const [savingEdit, setSavingEdit] = useState(false)

    const normalizedCurrentUserNames = useMemo(
        () =>
            (currentUserNames || [])
                .map(normalizeName)
                .filter(Boolean),
        [currentUserNames]
    )

    const isOwnComment = useCallback(
        (comment) => {
            const author = normalizeName(comment?.author)
            if (!author || !normalizedCurrentUserNames.length) return false
            return normalizedCurrentUserNames.includes(author)
        },
        [normalizedCurrentUserNames]
    )

    const canEditOwnComment = useCallback(
        (comment) => {
            if (readOnly || !onEditComment) return false
            return isOwnComment(comment)
        },
        [isOwnComment, onEditComment, readOnly]
    )

    const canDeleteOwnComment = useCallback(
        (comment) => {
            if (readOnly || !canDelete || !onDeleteComment) return false
            return isOwnComment(comment)
        },
        [canDelete, isOwnComment, onDeleteComment, readOnly]
    )

    const cancelEditing = useCallback(() => {
        setEditingCommentId(null)
        setEditMentionData({ value: '', comment: '', mentionDataList: [] })
        setClearEditEditor(false)
        setSavingEdit(false)
    }, [])

    useEffect(() => {
        if (!open) {
            setMentionData({ value: '', comment: '', mentionDataList: [] })
            setClearEditor(false)
            setActivityModalOpen(false)
            setPendingDeleteComment(null)
            cancelEditing()
            stickCommentsToBottomRef.current = true
            commentsScrollPreserveRef.current = null
        }
    }, [open, cardId, cancelEditing])

    useEffect(() => {
        if (clearEditor) {
            setClearEditor(false)
        }
    }, [clearEditor])

    useEffect(() => {
        if (clearEditEditor) {
            setClearEditEditor(false)
        }
    }, [clearEditEditor])

    useEffect(() => {
        const root = commentListScrollRef.current
        const sentinel = loadMoreSentinelRef.current

        if (!open || footerTab !== 'comments' || !root || !sentinel || !commentsHasMore) {
            return undefined
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return
                commentsScrollPreserveRef.current = {
                    height: root.scrollHeight,
                    top: root.scrollTop,
                }
                stickCommentsToBottomRef.current = false
                onLoadMoreComments?.()
            },
            { root, rootMargin: '80px', threshold: 0 }
        )

        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [
        open,
        footerTab,
        commentsHasMore,
        comments.length,
        onLoadMoreComments,
    ])

    useLayoutEffect(() => {
        if (!open || footerTab !== 'comments' || loadingComments) {
            if (loadingComments) stickCommentsToBottomRef.current = true
            return
        }

        const root = commentListScrollRef.current
        if (!root) return

        const preserved = commentsScrollPreserveRef.current
        if (preserved && !loadingMoreComments) {
            root.scrollTop =
                root.scrollHeight - preserved.height + preserved.top
            commentsScrollPreserveRef.current = null
            return
        }

        if (stickCommentsToBottomRef.current) {
            root.scrollTop = root.scrollHeight
        }
    }, [
        open,
        footerTab,
        loadingComments,
        loadingMoreComments,
        comments.length,
    ])

    const handleChangeMentions = useCallback((newValue, newPlainTextValue, mentions) => {
        setMentionData({
            value: newValue,
            comment: newPlainTextValue,
            mentionDataList: mentions,
        })
    }, [])

    const handleChangeEditMentions = useCallback(
        (newValue, newPlainTextValue, mentions) => {
            setEditMentionData({
                value: newValue,
                comment: newPlainTextValue,
                mentionDataList: mentions,
            })
        },
        []
    )

    const handleSubmitComment = useCallback(async () => {
        const html = String(mentionData?.value ?? '').trim()
        const plain = String(mentionData?.comment ?? '').trim()
        const text = html || plain
        if (!plain || postingComment) return

        stickCommentsToBottomRef.current = true
        setPostingComment(true)
        const result = await onPostComment?.(text)
        setPostingComment(false)

        if (!result?.success) return

        setMentionData({ value: '', comment: '', mentionDataList: [] })
        setClearEditor(true)
    }, [mentionData?.value, mentionData?.comment, onPostComment, postingComment])

    const startEditingComment = useCallback((comment) => {
        setPendingDeleteComment(null)
        setEditingCommentId(comment.id)
        setEditMentionData({
            value: comment.text || '',
            comment: stripCommentPlainText(comment.text),
            mentionDataList: [],
        })
    }, [])

    const handleSaveEditComment = useCallback(async () => {
        if (!editingCommentId || savingEdit) return

        const html = String(editMentionData?.value ?? '').trim()
        const plain = String(editMentionData?.comment ?? '').trim()
        const text = html || plain
        if (!plain) return

        setSavingEdit(true)
        const result = await onEditComment?.(editingCommentId, text)
        setSavingEdit(false)

        if (!result?.success) return
        cancelEditing()
    }, [
        cancelEditing,
        editMentionData?.comment,
        editMentionData?.value,
        editingCommentId,
        onEditComment,
        savingEdit,
    ])

    const handleConfirmDeleteComment = useCallback(async () => {
        const commentId = pendingDeleteComment?.id
        if (!commentId) return

        const result = await onDeleteComment?.(commentId)
        if (result?.success) {
            setPendingDeleteComment(null)
            if (String(editingCommentId) === String(commentId)) {
                cancelEditing()
            }
        }
    }, [
        cancelEditing,
        editingCommentId,
        onDeleteComment,
        pendingDeleteComment?.id,
    ])

    const tabItems = FOOTER_TABS.map((tab) => ({
        key: tab.key,
        label: tab.label,
        children:
            tab.key === 'activity' ? (
                <StoryMapActivityTab
                    key={cardId}
                    cardId={cardId}
                    open={open}
                    onLoadActivity={onLoadActivity}
                    isActive={open && footerTab === 'activity'}
                    variant="footer"
                />
            ) : (
                <div
                    ref={commentListScrollRef}
                    className={styles.footerCommentsList}
                >
                    {!loadingComments && commentsHasMore ? (
                        <div
                            ref={loadMoreSentinelRef}
                            className={styles.commentLoadMore}
                            aria-hidden
                        >
                            {loadingMoreComments ? (
                                <CommentListSkeleton rows={1} />
                            ) : null}
                        </div>
                    ) : null}

                    {loadingComments ? (
                        <CommentListSkeleton />
                    ) : comments.length > 0 ? (
                        <ul className={styles.footerCommentList}>
                            {comments.map((comment) => {
                                const showEdit = canEditOwnComment(comment)
                                const showDelete = canDeleteOwnComment(comment)
                                const showActions = showEdit || showDelete
                                const isEditing =
                                    String(editingCommentId) === String(comment.id)

                                return (
                                    <li
                                        key={comment.id}
                                        className={`${styles.footerCommentItem} ${
                                            showActions
                                                ? styles.footerCommentItemWithActions
                                                : ''
                                        } ${
                                            isEditing
                                                ? styles.footerCommentItemEditing
                                                : ''
                                        }`}
                                    >
                                        <Avatar
                                            size={28}
                                            className={styles.footerCommentAvatar}
                                            style={{
                                                backgroundColor: getAvatarColor(
                                                    comment.author
                                                ),
                                                flexShrink: 0,
                                            }}
                                        >
                                            {getInitials(comment.author)}
                                        </Avatar>
                                        <div className={styles.footerCommentBody}>
                                            <div className={styles.footerCommentMetaRow}>
                                                <div
                                                    className={
                                                        styles.footerCommentMeta
                                                    }
                                                >
                                                    <span
                                                        className={
                                                            styles.footerCommentAuthor
                                                        }
                                                    >
                                                        {comment.author}
                                                    </span>
                                                    {comment.createdAt ? (
                                                        <span
                                                            className={
                                                                styles.footerCommentTime
                                                            }
                                                        >
                                                            {comment.createdAt}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                {showActions && !isEditing ? (
                                                    <div
                                                        className={
                                                            styles.footerCommentActions
                                                        }
                                                    >
                                                        {showEdit ? (
                                                            <Tooltip
                                                                title="Edit"
                                                                placement="top"
                                                                mouseEnterDelay={0.35}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className={
                                                                        styles.footerCommentEditIconBtn
                                                                    }
                                                                    aria-label="Edit comment"
                                                                    onClick={() =>
                                                                        startEditingComment(
                                                                            comment
                                                                        )
                                                                    }
                                                                >
                                                                    <Icon
                                                                        icon="mdi:pencil-outline"
                                                                        width={15}
                                                                        aria-hidden
                                                                    />
                                                                </button>
                                                            </Tooltip>
                                                        ) : null}
                                                        {showDelete ? (
                                                            <Tooltip
                                                                title="Delete"
                                                                placement="top"
                                                                mouseEnterDelay={0.35}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className={
                                                                        styles.footerCommentDeleteIconBtn
                                                                    }
                                                                    aria-label="Delete comment"
                                                                    onClick={() =>
                                                                        setPendingDeleteComment(
                                                                            comment
                                                                        )
                                                                    }
                                                                >
                                                                    <Icon
                                                                        icon="mdi:trash-can-outline"
                                                                        width={15}
                                                                        aria-hidden
                                                                    />
                                                                </button>
                                                            </Tooltip>
                                                        ) : null}
                                                    </div>
                                                ) : null}
                                            </div>
                                            {isEditing ? (
                                                <div
                                                    className={
                                                        styles.footerCommentEditWrap
                                                    }
                                                >
                                                    <MentionEditor
                                                        key={`edit-comment-${comment.id}`}
                                                        projectId={projectId}
                                                        excludeUserIds={
                                                            editMentionData?.mentionDataList ||
                                                            []
                                                        }
                                                        handleChangeMentions={
                                                            handleChangeEditMentions
                                                        }
                                                        value={editMentionData?.value}
                                                        clearTrigger={clearEditEditor}
                                                        loadingButton={savingEdit}
                                                        onClickComment={
                                                            handleSaveEditComment
                                                        }
                                                        disabledButton={
                                                            !stripCommentPlainText(
                                                                editMentionData?.comment ||
                                                                    editMentionData?.value
                                                            )
                                                        }
                                                        disableMediaDrop
                                                        disableMentions
                                                    />
                                                    <div
                                                        className={
                                                            styles.footerCommentEditActions
                                                        }
                                                    >
                                                        <button
                                                            type="button"
                                                            className={
                                                                styles.footerCommentEditCancelBtn
                                                            }
                                                            onClick={cancelEditing}
                                                            disabled={savingEdit}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div
                                                    className={
                                                        styles.footerCommentText
                                                    }
                                                    dangerouslySetInnerHTML={{
                                                        __html: toCommentHtml(
                                                            comment.text
                                                        ),
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>
                    ) : (
                        <p className={styles.footerCommentsEmpty}>
                            No comments yet.
                        </p>
                    )}
                </div>
            ),
    }))

    return (
        <div className={styles.commentsFooter}>
            <div
                className={`${sprintTabStyles.tabsContainer} ${styles.commentsTabsWrap} ${
                    footerTab === 'activity'
                        ? styles.commentsTabsWrapActivity
                        : styles.commentsTabsWrapComments
                }`}
            >
                <Tabs
                    activeKey={footerTab}
                    onChange={onFooterTabChange}
                    items={tabItems}
                    destroyInactiveTabPane
                    className={sprintTabStyles.customTabs}
                    tabBarStyle={{ marginBottom: 0 }}
                    tabBarExtraContent={
                        footerTab === 'activity' ? (
                            <button
                                type="button"
                                className={styles.activityViewAllBtn}
                                onClick={() => setActivityModalOpen(true)}
                            >
                                View all
                            </button>
                        ) : null
                    }
                />
            </div>

            <div className={styles.commentInputFooter}>
                <MentionEditor
                    key={cardId || 'story-map-comment-editor'}
                    projectId={projectId}
                    excludeUserIds={mentionData?.mentionDataList || []}
                    handleChangeMentions={handleChangeMentions}
                    value={mentionData?.value}
                    clearTrigger={clearEditor}
                    loadingButton={postingComment}
                    onClickComment={handleSubmitComment}
                    disabledButton={!mentionData?.comment?.trim()}
                    disableMediaDrop
                    disableMentions
                />
            </div>

            <StoryMapActivityModal
                open={activityModalOpen}
                cardId={cardId}
                onClose={() => setActivityModalOpen(false)}
                onLoadActivity={onLoadActivity}
            />

            <StoryMapDeleteConfirm
                open={Boolean(pendingDeleteComment)}
                title="Delete this comment?"
                message="This comment will be permanently removed. This cannot be undone."
                detail={pendingDeleteComment?.author}
                confirmingText="Deleting..."
                onCancel={() => setPendingDeleteComment(null)}
                onConfirm={handleConfirmDeleteComment}
            />
        </div>
    )
}

export default StoryMapCommentsFooter
